// src/components/sessions/SessionDetail.jsx - FIXED PRINT FUNCTIONALITY
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  Users, 
  BookOpen,
  School,
  Briefcase,
  Target,
  MapPin,
  Edit,
  CheckCircle,
  ArrowLeft,
  BarChart3,
  TrendingUp,
  User,
  AlertCircle,
  Eye,
  Download,
  Plus,
  Activity,
  UserCheck,
  UserX,
  Clock3,
  Shield,
  Smile,
  Meh,
  Frown,
  Star,
  Heart,
  Zap,
  ThumbsUp,
  Filter,
  Search,
  Printer,
  GraduationCap
} from 'lucide-react';
import { SessionService } from '../../services/sessionService';
import { AttendanceService } from '../../services/attendanceService';
import { useAuth } from '../../contexts/AuthContext';

const SessionDetail = ({ session: sessionProp, onBack, onEdit, onRecordAttendance }) => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { userContexts, userProfile } = useAuth();
  const [session, setSession] = useState(sessionProp || null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(!sessionProp);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (sessionProp) {
      setSession(sessionProp);
      loadAttendanceData(sessionProp.id);
    } else if (sessionId) {
      loadSessionData();
    }
  }, [sessionId, sessionProp]);

  const loadSessionData = async () => {
    try {
      setLoading(true);
      const [sessionData, attendanceData] = await Promise.all([
        SessionService.getSession(sessionId),
        AttendanceService.getAttendanceRecords({ sessionId })
      ]);
      setSession(sessionData);
      setAttendanceRecords(attendanceData);
    } catch (err) {
      setError('Failed to load session details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAttendanceData = async (id) => {
    try {
      const attendanceData = await AttendanceService.getAttendanceRecords({ sessionId: id });
      setAttendanceRecords(attendanceData);
    } catch (err) {
      console.error('Failed to load attendance records:', err);
    }
  };

  // Helper functions
  const getSessionTypeIcon = (sessionContext) => {
    if (sessionContext?.type === 'institution') {
      return <School className="w-5 h-5 text-blue-500" />;
    }
    return <Briefcase className="w-5 h-5 text-green-500" />;
  };

  const getSessionCategoryColor = (category) => {
    switch (category) {
      case 'Lesson':
        return 'bg-blue-100 text-blue-800';
      case 'Assessment':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAttendanceIcon = (status) => {
    switch (status) {
      case 'Present':
        return <UserCheck className="w-4 h-4 text-green-600" />;
      case 'Late':
        return <Clock3 className="w-4 h-4 text-yellow-600" />;
      case 'Absent':
        return <UserX className="w-4 h-4 text-red-600" />;
      case 'Excused':
        return <Shield className="w-4 h-4 text-blue-600" />;
      default:
        return <User className="w-4 h-4 text-gray-400" />;
    }
  };

  const getEngagementIcon = (level) => {
    switch (level) {
      case 'H':
        return <Zap className="w-4 h-4 text-green-600" />;
      case 'M':
        return <ThumbsUp className="w-4 h-4 text-yellow-600" />;
      case 'L':
        return <Meh className="w-4 h-4 text-red-600" />;
      default:
        return <Activity className="w-4 h-4 text-gray-400" />;
    }
  };

  const getBehaviourIcon = (level) => {
    switch (level) {
      case 'E':
        return <Star className="w-4 h-4 text-green-600" />;
      case 'G':
        return <Smile className="w-4 h-4 text-blue-600" />;
      case 'S':
        return <Meh className="w-4 h-4 text-yellow-600" />;
      case 'N':
        return <Frown className="w-4 h-4 text-red-600" />;
      default:
        return <User className="w-4 h-4 text-gray-400" />;
    }
  };

  const calculateOverallStats = () => {
    if (attendanceRecords.length === 0) {
      return { 
        averageAttendance: 0, 
        totalRecords: 0, 
        totalLearners: 0,
        engagementStats: {}, 
        behaviourStats: {} 
      };
    }

    const totalAttendance = attendanceRecords.reduce((sum, record) => 
      sum + parseFloat(record.stats?.attendanceRate || 0), 0);
    const averageAttendance = totalAttendance / attendanceRecords.length;

    const totalLearners = attendanceRecords.reduce((sum, record) => 
      sum + (record.attendanceData?.length || 0), 0);

    const engagementStats = { H: 0, M: 0, L: 0 };
    const behaviourStats = { E: 0, G: 0, S: 0, N: 0 };

    attendanceRecords.forEach(record => {
      record.attendanceData?.forEach(learner => {
        if (learner.engagement && engagementStats[learner.engagement] !== undefined) {
          engagementStats[learner.engagement]++;
        }
        if (learner.behaviour && behaviourStats[learner.behaviour] !== undefined) {
          behaviourStats[learner.behaviour]++;
        }
      });
    });

    return {
      averageAttendance: averageAttendance.toFixed(1),
      totalRecords: attendanceRecords.length,
      totalLearners,
      engagementStats,
      behaviourStats
    };
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(session);
    } else {
      navigate(`/sessions/edit/${session.id}`);
    }
  };

  const handleRecordAttendance = () => {
    if (onRecordAttendance) {
      onRecordAttendance(session);
    } else {
      navigate(`/attendance/record/${session.id}`);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/sessions');
    }
  };

  // Print functionality
  const handlePrint = () => {
    // Switch to attendance tab before printing to ensure data is visible
    setActiveTab('attendance');
    // Small delay to let the DOM update
    setTimeout(() => {
      window.print();
    }, 100);
  };

  // Filter attendance records for search and status
  const getFilteredLearners = (attendanceData) => {
    if (!attendanceData) return [];
    
    return attendanceData.filter(learner => {
      const matchesSearch = searchTerm === '' || 
        `${learner.learnerName} ${learner.learnerSurname}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (learner.school && learner.school.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesStatus = filterStatus === 'all' || learner.attendance === filterStatus;
      
      return matchesSearch && matchesStatus;
    });
  };

  const stats = calculateOverallStats();

  // Check if attendance records exist - for button disabling
  const hasAttendanceRecords = attendanceRecords.length > 0;

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading session details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <span className="text-gray-600">Session not found</span>
        </div>
      </div>
    );
  }

  const context = userContexts?.find(c => c.id === session.sessionContext);

  return (
    <>
      {/* Comprehensive Print Styles */}
      <style jsx global>{`
        @media print {
          /* Page setup */
          @page {
            size: A4 landscape;
            margin: 0.5in;
          }
          
          /* Hide all dashboard elements */
          header,
          nav,
          .no-print,
          button:not(.print-keep),
          input,
          select,
          .fixed,
          .sticky,
          .shadow-lg,
          .shadow-sm,
          .border,
          .rounded-lg,
          .rounded-md,
          footer {
            display: none !important;
          }
          
          /* Reset body and html for print */
          html, body {
            height: auto !important;
            overflow: visible !important;
            background: white !important;
            color: black !important;
            font-family: 'Times New Roman', serif !important;
            font-size: 12pt !important;
            line-height: 1.4 !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          /* Main container */
          #root,
          .App,
          main {
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
          
          /* Show only print content */
          .print-only {
            display: block !important;
          }
          
          .print-container {
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 10px !important;
            background: white !important;
          }
          
          /* Print header styling */
          .print-header {
            text-align: center;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid #000;
          }
          
          .print-header h1 {
            font-size: 24pt !important;
            font-weight: bold !important;
            margin: 0 0 10px 0 !important;
            color: black !important;
          }
          
          .print-header h2 {
            font-size: 18pt !important;
            font-weight: bold !important;
            margin: 0 0 5px 0 !important;
            color: black !important;
          }
          
          .print-header p {
            font-size: 14pt !important;
            margin: 5px 0 !important;
            color: black !important;
          }
          
          /* Table styling */
          .print-table {
            width: 100% !important;
            border-collapse: collapse !important;
            margin: 20px 0 !important;
            font-size: 10pt !important;
          }
          
          .print-table th,
          .print-table td {
            border: 1px solid #000 !important;
            padding: 6px !important;
            text-align: left !important;
            vertical-align: top !important;
            background: transparent !important;
          }
          
          .print-table th {
            background: #f0f0f0 !important;
            font-weight: bold !important;
            font-size: 9pt !important;
            text-align: center !important;
          }
          
          .print-table tbody tr:nth-child(even) {
            background: #f9f9f9 !important;
          }
          
          /* Stats section */
          .print-stats {
            display: flex !important;
            justify-content: space-around !important;
            margin: 15px 0 !important;
            padding: 10px !important;
            border: 1px solid #ccc !important;
            background: #f9f9f9 !important;
            font-size: 12pt !important;
          }
          
          .print-stats > div {
            text-align: center !important;
          }
          
          /* Signature area */
          .print-signature {
            margin-top: 40px !important;
            display: flex !important;
            justify-content: space-between !important;
          }
          
          .signature-line {
            border-bottom: 1px solid #000 !important;
            width: 200px !important;
            margin-bottom: 5px !important;
          }
          
          /* Force attendance tab content to show */
          .attendance-content {
            display: block !important;
            width: 100% !important;
          }
          
          /* Hide icons in print */
          svg {
            display: none !important;
          }
          
          /* Ensure text is readable */
          * {
            color: black !important;
            background: transparent !important;
          }
        }
      `}</style>

      <div className="max-w-7xl mx-auto p-4 sm:p-6 print-container">
        {/* Print Header - Only visible in print */}
        <div className="hidden print-only print-header">
          <h1>ATTENDANCE RECORD</h1>
          <h2>{session.sessionTitle}</h2>
          <p>{session.subject} - Grade {session.grades?.join(', ') || 'N/A'}</p>
          <p>
            {session.sessionDate ? 
              new Date(session.sessionDate).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 
              'Date not specified'
            }
            {session.duration && ` | Duration: ${session.duration} minutes`}
          </p>
          <p>Teacher: {userProfile?.firstName} {userProfile?.lastName}</p>
        </div>

        {/* Screen Header - Hidden in print */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 no-print">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors self-start"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            
            <div className="flex-1 lg:mx-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                {getSessionTypeIcon(session.sessionContext)}
                <span className="text-sm text-gray-500">{context?.name}</span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSessionCategoryColor(session.sessionCategory)} self-start sm:self-auto`}>
                  {session.sessionCategory}
                </span>
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">{session.sessionTitle}</h1>
              <p className="text-gray-600">{session.sessionType}</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={handleEdit}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Edit className="w-4 h-4" />
                <span>Edit Session</span>
              </button>
              
              {/* Conditional Attendance Button */}
              <button
                onClick={handleRecordAttendance}
                disabled={hasAttendanceRecords}
                className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  hasAttendanceRecords
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                title={hasAttendanceRecords ? 'Attendance already recorded for this session' : 'Record attendance for this session'}
              >
                <CheckCircle className="w-4 h-4" />
                <span>{hasAttendanceRecords ? 'Attendance Recorded' : 'Record Attendance'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Session Info Cards - Hidden in print */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 no-print">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-blue-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-gray-500">Session Date</p>
                <p className="font-semibold text-sm sm:text-base truncate">
                  {session.sessionDate ? 
                    new Date(session.sessionDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    }) : 
                    'Not specified'
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-green-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-gray-500">Duration</p>
                <p className="font-semibold text-sm sm:text-base">
                  {session.duration ? `${session.duration} mins` : 'Not specified'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-purple-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-gray-500">Expected Learners</p>
                <p className="font-semibold text-sm sm:text-base">
                  {session.expectedLearners || session.sessionLearners?.length || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-orange-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-gray-500">Subject</p>
                <p className="font-semibold text-sm sm:text-base truncate">
                  {session.subject} - Grade {session.grades?.join(', ') || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs - Hidden in print */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 no-print">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: Target },
                { id: 'attendance', label: 'Attendance Records', icon: Activity },
                { id: 'analytics', label: 'Analytics', icon: BarChart3 }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                  {tab.id === 'attendance' && attendanceRecords.length > 0 && (
                    <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                      {attendanceRecords.length}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab - Hidden in print */}
            {activeTab === 'overview' && (
              <div className="space-y-6 no-print">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Session Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Session Details</h3>
                    <div className="space-y-3">
                      {session.learningObjectives && (
                        <div className="flex gap-3">
                          <Target className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-gray-900 mb-1">Learning Objectives</p>
                            <p className="text-gray-600 text-sm">{session.learningObjectives}</p>
                          </div>
                        </div>
                      )}
                      
                      {session.location && (
                        <div className="flex gap-3">
                          <MapPin className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-gray-900 mb-1">Location</p>
                            <p className="text-gray-600 text-sm">{session.location}</p>
                          </div>
                        </div>
                      )}

                      {session.sessionNotes && (
                        <div className="flex gap-3">
                          <BookOpen className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-gray-900 mb-1">Notes</p>
                            <p className="text-gray-600 text-sm">{session.sessionNotes}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Quick Stats</h3>
                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-600">Attendance Records</span>
                        <span className="text-lg font-bold text-blue-600">{stats.totalRecords}</span>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-600">Average Attendance</span>
                        <span className="text-lg font-bold text-green-600">{stats.averageAttendance}%</span>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-600">Total Learner Entries</span>
                        <span className="text-lg font-bold text-purple-600">{stats.totalLearners}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced Attendance Tab - This will show in print */}
            {activeTab === 'attendance' && (
              <div className="space-y-6 attendance-content">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 no-print">
                  <h3 className="text-lg font-semibold text-gray-900">Attendance Records</h3>
                  <div className="flex gap-2">
                    {/* Print Button */}
                    {attendanceRecords.length > 0 && (
                      <button
                        onClick={handlePrint}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <Printer className="w-4 h-4" />
                        <span>Print</span>
                      </button>
                    )}
                    
                    {/* Conditional Record Attendance Button */}
                    <button
                      onClick={handleRecordAttendance}
                      disabled={hasAttendanceRecords}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                        hasAttendanceRecords
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                      title={hasAttendanceRecords ? 'Attendance already recorded for this session' : 'Record attendance for this session'}
                    >
                      <Plus className="w-4 h-4" />
                      <span>{hasAttendanceRecords ? 'Already Recorded' : 'Record Attendance'}</span>
                    </button>
                  </div>
                </div>

                {attendanceRecords.length > 0 ? (
                  <div className="space-y-6">
                    {attendanceRecords.map((record, recordIndex) => (
                      <div key={record.id} className="bg-gray-50 rounded-lg overflow-hidden">
                        {/* Record Header - Hidden in print */}
                        <div className="px-6 py-4 bg-white border-b border-gray-200 no-print">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <Calendar className="w-5 h-5 text-gray-400" />
                              <div>
                                <h4 className="font-medium text-gray-900">
                                  Attendance Record #{recordIndex + 1}
                                </h4>
                                <p className="text-sm text-gray-500">
                                  Recorded on {new Date(record.recordedAt).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>
                            
                            {/* Record Stats */}
                            <div className="flex flex-wrap gap-2">
                              <div className={`px-3 py-1 text-xs font-medium rounded-full ${
                                parseFloat(record.stats?.attendanceRate || 0) >= 90 ? 'bg-green-100 text-green-800' :
                                parseFloat(record.stats?.attendanceRate || 0) >= 75 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {record.stats?.attendanceRate || 0}% Attendance
                              </div>
                              <div className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                {record.attendanceData?.length || 0} Learners
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Print Stats - Only visible in print */}
                        <div className="hidden print-only print-stats">
                          <div>
                            <strong>Total Learners:</strong> {record.attendanceData?.length || 0}
                          </div>
                          <div>
                            <strong>Present:</strong> {record.stats?.present || 0}
                          </div>
                          <div>
                            <strong>Absent:</strong> {record.stats?.absent || 0}
                          </div>
                          <div>
                            <strong>Attendance Rate:</strong> {record.stats?.attendanceRate || 0}%
                          </div>
                        </div>

                        {/* Filters - Hidden in print */}
                        {record.attendanceData && record.attendanceData.length > 0 && (
                          <div className="px-6 py-4 bg-white border-b border-gray-200 no-print">
                            <div className="flex flex-col sm:flex-row gap-4">
                              <div className="flex-1">
                                <div className="relative">
                                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                  <input
                                    type="text"
                                    placeholder="Search learners or schools..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Filter className="w-4 h-4 text-gray-400" />
                                <select
                                  value={filterStatus}
                                  onChange={(e) => setFilterStatus(e.target.value)}
                                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                  <option value="all">All Status</option>
                                  <option value="Present">Present</option>
                                  <option value="Late">Late</option>
                                  <option value="Absent">Absent</option>
                                  <option value="Excused">Excused</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Enhanced Learner Data Table with School Information */}
                        {record.attendanceData && record.attendanceData.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full print-table">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="sticky left-0 bg-gray-100 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-[200px] z-10">
                                    Learner
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                                    School
                                  </th>
                                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                                    Status
                                  </th>
                                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                                    Engagement
                                  </th>
                                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                                    Behaviour
                                  </th>
                                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                                    Collaboration
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                                    Notes
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {getFilteredLearners(record.attendanceData).map((learner, learnerIndex) => (
                                  <tr key={learner.id || learnerIndex} className="hover:bg-gray-50">
                                    <td className="sticky left-0 bg-white px-6 py-4 border-r border-gray-200 z-10">
                                      <div className="flex items-center gap-3">
                                        <User className="w-8 h-8 text-gray-400 no-print" />
                                        <div>
                                          <p className="font-medium text-gray-900">
                                            {learner.learnerName} {learner.learnerSurname}
                                          </p>
                                          <p className="text-sm text-gray-500">
                                            Grade {learner.grade}
                                          </p>
                                        </div>
                                      </div>
                                    </td>
                                    
                                    {/* School Column */}
                                    <td className="px-4 py-4">
                                      <div className="flex items-center gap-2">
                                        <GraduationCap className="w-4 h-4 text-gray-400 no-print" />
                                        <span className="text-sm text-gray-600">
                                          {learner.school || 'Not specified'}
                                        </span>
                                      </div>
                                    </td>
                                    
                                    <td className="px-4 py-4 text-center">
                                      <div className="flex items-center justify-center gap-2">
                                        <span className="no-print">{getAttendanceIcon(learner.attendance)}</span>
                                        <span className={`text-sm font-medium ${
                                          learner.attendance === 'Present' ? 'text-green-700' :
                                          learner.attendance === 'Late' ? 'text-yellow-700' :
                                          learner.attendance === 'Absent' ? 'text-red-700' :
                                          learner.attendance === 'Excused' ? 'text-blue-700' :
                                          'text-gray-700'
                                        }`}>
                                          {learner.attendance || 'N/A'}
                                        </span>
                                      </div>
                                    </td>
                                    
                                    <td className="px-4 py-4 text-center">
                                      <div className="flex items-center justify-center gap-2">
                                        <span className="no-print">{getEngagementIcon(learner.engagement)}</span>
                                        <span className={`text-sm ${
                                          learner.engagement === 'H' ? 'text-green-700' :
                                          learner.engagement === 'M' ? 'text-yellow-700' :
                                          learner.engagement === 'L' ? 'text-red-700' :
                                          'text-gray-500'
                                        }`}>
                                          {learner.engagement === 'H' ? 'High' :
                                           learner.engagement === 'M' ? 'Medium' :
                                           learner.engagement === 'L' ? 'Low' : 'N/A'}
                                        </span>
                                      </div>
                                    </td>
                                    
                                    <td className="px-4 py-4 text-center">
                                      <div className="flex items-center justify-center gap-2">
                                        <span className="no-print">{getBehaviourIcon(learner.behaviour)}</span>
                                        <span className={`text-sm ${
                                          learner.behaviour === 'E' ? 'text-green-700' :
                                          learner.behaviour === 'G' ? 'text-blue-700' :
                                          learner.behaviour === 'S' ? 'text-yellow-700' :
                                          learner.behaviour === 'N' ? 'text-red-700' :
                                          'text-gray-500'
                                        }`}>
                                          {learner.behaviour === 'E' ? 'Excellent' :
                                           learner.behaviour === 'G' ? 'Good' :
                                           learner.behaviour === 'S' ? 'Satisfactory' :
                                           learner.behaviour === 'N' ? 'Needs Improvement' : 'N/A'}
                                        </span>
                                      </div>
                                    </td>
                                    
                                    <td className="px-4 py-4 text-center">
                                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                        learner.collaboration === 'H' ? 'bg-green-100 text-green-800' :
                                        learner.collaboration === 'M' ? 'bg-yellow-100 text-yellow-800' :
                                        learner.collaboration === 'L' ? 'bg-red-100 text-red-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {learner.collaboration === 'H' ? 'High' :
                                         learner.collaboration === 'M' ? 'Medium' :
                                         learner.collaboration === 'L' ? 'Low' : 'N/A'}
                                      </span>
                                    </td>
                                    
                                    <td className="px-4 py-4">
                                      <p className="text-sm text-gray-600">
                                        {learner.notes || '-'}
                                      </p>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="p-8 text-center no-print">
                            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">No learner data recorded for this session</p>
                          </div>
                        )}

                        {/* Print Signature Section - Only visible in print */}
                        <div className="hidden print-only print-signature">
                          <div className="text-center">
                            <div className="signature-line"></div>
                            <p className="text-sm">Teacher Signature</p>
                          </div>
                          <div className="text-center">
                            <div className="signature-line"></div>
                            <p className="text-sm">Date</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 no-print">
                    <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">No Attendance Records</h4>
                    <p className="text-gray-500 mb-6">Start by recording attendance for this session.</p>
                    <button
                      onClick={handleRecordAttendance}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Record First Attendance</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Analytics Tab - Hidden in print */}
            {activeTab === 'analytics' && (
              <div className="space-y-6 no-print">
                <h3 className="text-lg font-semibold text-gray-900">Session Analytics</h3>
                
                {stats.totalRecords > 0 ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div className="p-6 border border-gray-200 rounded-lg bg-gradient-to-br from-blue-50 to-white">
                        <div className="flex items-center gap-3 mb-3">
                          <BarChart3 className="w-6 h-6 text-blue-500" />
                          <span className="font-medium text-gray-900">Average Attendance</span>
                        </div>
                        <div className="text-3xl font-bold text-blue-600 mb-1">{stats.averageAttendance}%</div>
                        <p className="text-sm text-gray-600">Across {stats.totalRecords} sessions</p>
                      </div>

                      <div className="p-6 border border-gray-200 rounded-lg bg-gradient-to-br from-green-50 to-white">
                        <div className="flex items-center gap-3 mb-3">
                          <TrendingUp className="w-6 h-6 text-green-500" />
                          <span className="font-medium text-gray-900">Total Records</span>
                        </div>
                        <div className="text-3xl font-bold text-green-600 mb-1">{stats.totalRecords}</div>
                        <p className="text-sm text-gray-600">{stats.totalLearners} learner entries</p>
                      </div>

                      <div className="p-6 border border-gray-200 rounded-lg bg-gradient-to-br from-purple-50 to-white">
                        <div className="flex items-center gap-3 mb-3">
                          <Activity className="w-6 h-6 text-purple-500" />
                          <span className="font-medium text-gray-900">Latest Record</span>
                        </div>
                        <div className="text-lg font-bold text-purple-600 mb-1">
                          {attendanceRecords[0] ? new Date(attendanceRecords[0].recordedAt).toLocaleDateString() : 'N/A'}
                        </div>
                        <p className="text-sm text-gray-600">Most recent session</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">No Analytics Available</h4>
                    <p className="text-gray-500">Analytics will be available once you have attendance records.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Force show attendance records in print if they exist */}
        {attendanceRecords.length > 0 && (
          <div className="hidden print-only">
            {attendanceRecords.map((record, recordIndex) => (
              <div key={`print-${record.id}`}>
                <div className="print-stats">
                  <div><strong>Total:</strong> {record.attendanceData?.length || 0}</div>
                  <div><strong>Present:</strong> {record.stats?.present || 0}</div>
                  <div><strong>Absent:</strong> {record.stats?.absent || 0}</div>
                  <div><strong>Rate:</strong> {record.stats?.attendanceRate || 0}%</div>
                </div>

                <table className="print-table">
                  <thead>
                    <tr>
                      <th>Learner</th>
                      <th>School</th>
                      <th>Status</th>
                      <th>Engagement</th>
                      <th>Behaviour</th>
                      <th>Collaboration</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(record.attendanceData || []).map((learner, learnerIndex) => (
                      <tr key={`print-learner-${learnerIndex}`}>
                        <td>
                          {learner.learnerName} {learner.learnerSurname}
                          <br />
                          <small>Grade {learner.grade}</small>
                        </td>
                        <td>{learner.school || 'Not specified'}</td>
                        <td>{learner.attendance || 'N/A'}</td>
                        <td>
                          {learner.engagement === 'H' ? 'High' :
                           learner.engagement === 'M' ? 'Medium' :
                           learner.engagement === 'L' ? 'Low' : 'N/A'}
                        </td>
                        <td>
                          {learner.behaviour === 'E' ? 'Excellent' :
                           learner.behaviour === 'G' ? 'Good' :
                           learner.behaviour === 'S' ? 'Satisfactory' :
                           learner.behaviour === 'N' ? 'Needs Improvement' : 'N/A'}
                        </td>
                        <td>
                          {learner.collaboration === 'H' ? 'High' :
                           learner.collaboration === 'M' ? 'Medium' :
                           learner.collaboration === 'L' ? 'Low' : 'N/A'}
                        </td>
                        <td>{learner.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="print-signature">
                  <div className="text-center">
                    <div className="signature-line"></div>
                    <p>Teacher Signature</p>
                  </div>
                  <div className="text-center">
                    <div className="signature-line"></div>
                    <p>Date</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default SessionDetail;